import { Link } from 'react-router';
import { TableOfContents } from '@/components/TableOfContents';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        {title}
      </h2>
      <div className="prose-custom">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-8 scroll-mt-20">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {title}
      </h3>
      <div className="prose-custom">{children}</div>
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{children}</p>;
}

function CodeBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {title && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400">
          {title}
        </div>
      )}
      <pre className="px-4 py-3 bg-gray-950 text-gray-300 text-sm font-mono overflow-x-auto leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 dark:text-gray-400 font-mono text-xs">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ type, children }: { type: 'info' | 'tip' | 'warning'; children: React.ReactNode }) {
  const styles = {
    info: 'border-info-500 bg-info-50 dark:bg-info-500/10 text-info-600 dark:text-info-400',
    tip: 'border-success-500 bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-400',
    warning: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400',
  };
  const labels = { info: 'Note', tip: 'Tip', warning: 'Warning' };
  return (
    <div className={`my-4 p-4 rounded-xl border-l-4 ${styles[type]}`}>
      <div className="font-semibold text-sm mb-1">{labels[type]}</div>
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
}

export function PersonalAccessTokensPage() {
  useDocumentMeta({
    title: 'Personal Access Tokens: LogPulse Docs',
    description: 'Create and manage scoped personal access tokens (PATs) for programmatic access to the LogPulse management API.',
  });

  const toc = [
    { id: 'overview', label: 'Overview' },
    { id: 'pat-vs-api-key', label: 'PATs vs. Ingest API Keys', level: 1 },
    { id: 'token-format', label: 'Token Format' },
    { id: 'scopes', label: 'Scopes' },
    { id: 'creating-a-token', label: 'Creating a Token' },
    { id: 'create-ui', label: 'In the Dashboard', level: 1 },
    { id: 'create-api', label: 'Via the API', level: 1 },
    { id: 'using-a-token', label: 'Using a Token' },
    { id: 'expiration-rotation', label: 'Expiration & Rotation' },
    { id: 'revoking', label: 'Revoking a Token' },
    { id: 'auth-errors', label: 'Authentication Errors' },
    { id: 'security-best-practices', label: 'Security Best Practices' },
  ];

  return (
    <div className="flex gap-8">
      <div className="flex-1 min-w-0">
        <Section id="overview" title="Personal Access Tokens">
          <P>
            Personal Access Tokens (PATs) provide programmatic access to the LogPulse
            management API, the endpoints you would otherwise reach through the dashboard,
            such as the <Link to="/docs/api/ai-evaluations" className="text-primary-600 dark:text-primary-400 hover:underline">AI Evaluations API</Link>.
            A PAT is tied to your personal account and to one organization, and is limited
            to the scopes you select when creating it. You create and manage tokens in the
            dashboard under{' '}
            <a
              href="https://app.logpulse.io/profile?tab=tokens"
              className="text-primary-600 dark:text-primary-400 hover:underline"
            >
              Profile → Access Tokens
            </a>.
          </P>
          <P>
            Because a PAT belongs to a person (not to the organization as a whole), every
            action performed with it is attributable to you in the audit log, and tokens
            are automatically removed when the owning account is deleted. There are no
            orphaned credentials after offboarding.
          </P>

          <SubSection id="pat-vs-api-key" title="PATs vs. Ingest API Keys">
            <P>
              LogPulse has two distinct credential types. They are not interchangeable:
              an ingest key is rejected on management endpoints, and a PAT is rejected on
              ingest endpoints.
            </P>
            <Table
              headers={['', 'Personal Access Token', 'Ingest API Key']}
              rows={[
                ['Prefix', 'lpat_', 'lp_'],
                ['Bound to', 'A user + organization', 'The organization'],
                ['Permissions', 'Scoped (resource:action)', 'Ingest only, no scopes'],
                ['Used for', 'Management API (evaluations, automation)', 'Log ingestion (agents, OTLP, HTTP API)'],
                ['Managed in', 'Profile → Access Tokens', 'Integrations → HTTP API'],
              ]}
            />
            <Callout type="info">
              For sending logs to LogPulse, use an ingest API key, see the{' '}
              <Link to="/docs/integrations/http-api" className="underline">HTTP API documentation</Link>.
              This page covers PATs for the management API only.
            </Callout>
          </SubSection>
        </Section>

        <Section id="token-format" title="Token Format">
          <P>
            Tokens start with the <code>lpat_</code> prefix followed by 48 hexadecimal
            characters. The prefix makes PATs easy to recognize in configuration files and
            secret scanners, and distinguishes them from ingest keys (<code>lp_</code>).
          </P>
          <CodeBlock title="Token format">
{`lpat_a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4`}
          </CodeBlock>
          <P>
            LogPulse stores only a SHA-256 hash of the token. The full token is shown
            exactly once, at creation time. It cannot be retrieved afterwards. The
            dashboard displays only the first 13 characters (the token prefix) so you can
            tell tokens apart.
          </P>
        </Section>

        <Section id="scopes" title="Scopes">
          <P>
            Scopes follow a <code>resource:action</code> convention. A token can hold any
            combination of scopes; endpoints require specific scopes and reject tokens
            that lack them with HTTP 403. A token never grants more than your own
            permissions in the organization. Scopes can only narrow access, not widen it.
          </P>
          <Table
            headers={['Scope', 'Grants', 'Typical use']}
            rows={[
              ['evaluations:read', 'Read evaluations and run results', 'Reporting, dashboards over verdicts'],
              ['evaluations:write', 'Create, update and delete evaluations', 'Infrastructure-as-code, sync scripts'],
              ['evaluations:run', 'Trigger evaluation runs, including ad-hoc runs', 'CI pipelines, detection testing'],
              ['logs:read', 'Search logs over the HTTP API and the MCP server', 'Scheduled reports, external monitoring, AI agents'],
            ]}
          />
          <P>
            On the HTTP API a token carries the access of the user who created it, so a token
            made by an owner reads everything that owner can read. Membership and role are
            checked on every request, never taken from when the token was minted, so a role
            change or a removal from the organization takes effect at once. Create tokens from
            an account that holds only the access the job needs.
          </P>
          <P>
            The MCP server is stricter: there a token is limited to the indexes and namespaces
            the roles assigned to its owner grant, and it does not inherit owner or admin access.
          </P>
          <Callout type="info">
            More resources (alerts, dashboards, and others) will gain scopes over time as
            the management API expands. Existing tokens are unaffected: a token only ever
            holds the scopes it was created with.
          </Callout>
        </Section>

        <Section id="creating-a-token" title="Creating a Token">
          <SubSection id="create-ui" title="In the Dashboard">
            <P>
              Tokens are created in your LogPulse settings: navigate to{' '}
              <a
                href="https://app.logpulse.io/profile?tab=tokens"
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                Profile → Access Tokens
              </a>{' '}
              and click Create token. Then:
            </P>
            <P>
              1. Enter a descriptive name (for example "CI pipeline" or "Detection test
              runner"). The name is how you will recognize the token later, so name it
              after the system that will use it.
            </P>
            <P>
              2. Select the scopes the token needs. Follow least privilege: a CI job that
              only triggers evaluation runs needs <code>evaluations:run</code> and nothing
              else.
            </P>
            <P>
              3. Click Create token. The full token is displayed once. Copy it now and
              store it in your secret manager. After you close the dialog, only the prefix
              remains visible.
            </P>
            <Callout type="warning">
              The token is shown only once. If you lose it, revoke it and create a new
              one. There is no way to recover the secret.
            </Callout>
          </SubSection>

          <SubSection id="create-api" title="Via the API">
            <P>
              Token management endpoints are available under{' '}
              <code>/api/v1/personal-access-tokens</code>. These endpoints use your
              dashboard session for authentication (a PAT cannot create other PATs), so
              they are primarily useful from browser-based tooling. For most automation
              setups, create tokens in the dashboard.
            </P>
            <Table
              headers={['Method', 'Endpoint', 'Description']}
              rows={[
                ['GET', '/api/v1/personal-access-tokens', 'List your active tokens in the current organization'],
                ['POST', '/api/v1/personal-access-tokens', 'Create a token; the response contains the secret once'],
                ['DELETE', '/api/v1/personal-access-tokens/:id', 'Revoke a token immediately'],
              ]}
            />
            <CodeBlock title="POST /api/v1/personal-access-tokens: request body">
{`{
  "name": "CI pipeline",
  "scopes": ["evaluations:run"],
  "expiresAt": "2026-12-31T00:00:00Z"   // optional
}`}
            </CodeBlock>
            <CodeBlock title="Response (the secret is returned exactly once)">
{`{
  "data": {
    "token": {
      "id": "9b2f1c4e-...",
      "name": "CI pipeline",
      "tokenPrefix": "lpat_a1b2c3d4",
      "scopes": ["evaluations:run"],
      "lastUsedAt": null,
      "expiresAt": "2026-12-31T00:00:00.000Z",
      "createdAt": "2026-06-12T09:30:00.000Z"
    },
    "secret": "lpat_a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4"
  }
}`}
            </CodeBlock>
          </SubSection>
        </Section>

        <Section id="using-a-token" title="Using a Token">
          <P>
            Send the token as a Bearer token in the <code>Authorization</code> header on
            every request to a PAT-protected endpoint:
          </P>
          <CodeBlock title="Authenticated request">
{`curl https://api.logpulse.io/api/v1/evaluations/run-adhoc \\
  -H "Authorization: Bearer lpat_your_token_here" \\
  -H "Content-Type: application/json" \\
  -d '{ ... }'`}
          </CodeBlock>
          <P>
            Each successful use updates the token's "last used" timestamp, visible in
            Profile → Access Tokens. Use it to spot tokens that are no longer in use and
            can be revoked.
          </P>
        </Section>

        <Section id="expiration-rotation" title="Expiration & Rotation">
          <P>
            Tokens can optionally be given an expiration date at creation time. An expired
            token is rejected with HTTP 401 and code <code>PAT_EXPIRED</code>. Tokens
            without an expiration date remain valid until revoked.
          </P>
          <P>
            To rotate a token: create a new token with the same scopes, update the consuming
            system to use it, then revoke the old token. Because tokens are independent of
            each other, this gives zero-downtime rotation.
          </P>
          <Callout type="tip">
            Set an expiration date on tokens used in experiments or short-lived projects,
            and establish a rotation schedule (for example every 90 days) for long-lived
            automation tokens.
          </Callout>
        </Section>

        <Section id="revoking" title="Revoking a Token">
          <P>
            Revoke a token from Profile → Access Tokens using the delete button next to
            the token, or via <code>DELETE /api/v1/personal-access-tokens/:id</code>.
            Revocation takes effect immediately: any request using the revoked token
            receives HTTP 401 with code <code>PAT_REVOKED</code>. Revocation cannot be
            undone.
          </P>
          <P>
            You can only revoke your own tokens. Token creation and revocation are both
            recorded in the organization's audit log.
          </P>
          <Callout type="warning">
            Revoking a token immediately breaks every integration that uses it. Make sure
            consuming systems have been switched to a replacement token first.
          </Callout>
        </Section>

        <Section id="auth-errors" title="Authentication Errors">
          <P>
            PAT-protected endpoints return the following errors. The <code>code</code> field
            in the response body is stable and safe to match on programmatically.
          </P>
          <Table
            headers={['HTTP status', 'Code', 'Meaning']}
            rows={[
              ['401', 'UNAUTHORIZED', 'No Authorization header, or not a Bearer token'],
              ['401', 'INVALID_PAT', 'Token is not an lpat_ token, or does not exist'],
              ['401', 'PAT_REVOKED', 'Token has been revoked'],
              ['401', 'PAT_EXPIRED', 'Token has passed its expiration date'],
              ['403', 'INSUFFICIENT_SCOPE', 'Token is valid but missing a required scope'],
            ]}
          />
          <CodeBlock title="Example error response">
{`{
  "error": "Insufficient token scope",
  "code": "INSUFFICIENT_SCOPE",
  "message": "This token is missing the required scope(s): evaluations:run"
}`}
          </CodeBlock>
        </Section>

        <Section id="security-best-practices" title="Security Best Practices">
          <P>
            Treat a PAT like a password: anyone holding it can act on your behalf within
            its scopes.
          </P>
          <P>
            Use one token per system. Give your CI pipeline, your sync script, and your
            reporting job each their own token. This limits blast radius and lets you
            revoke one integration without breaking the others.
          </P>
          <P>
            Grant the minimum scopes. A token that only triggers evaluation runs should
            hold only <code>evaluations:run</code>.
          </P>
          <P>
            Store tokens in a secret manager (GitHub Actions secrets, Vault, or your
            platform's equivalent) and inject them as environment variables. Never commit
            tokens to version control.
          </P>
          <P>
            Review your tokens periodically. The "last used" column in Profile → Access
            Tokens shows which tokens are still active. Revoke anything you no longer
            recognize or need.
          </P>
        </Section>
      </div>

      <TableOfContents items={toc} />
    </div>
  );
}
